import React from 'react';
import { apiService } from '../../services/api';
import * as fmt from '../../lib/format';
import { StatusPill } from './PlanTable';
import { RoadmapData } from './types';
import { usePlanView } from './usePlanView';
import ViewState from './ViewState';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

// The goals across the quarter's sprints: a row per goal, a column per sprint, and in each cell how many of the
// goal's tickets are in that sprint and how many of those are done. It scrolls inside its own box with the goal pinned.
export default function RoadmapGrid({ quarter, projectKey, onOpen }: { quarter: string | null; projectKey: string | null; onOpen: (key: string) => void }) {
  const { data, error, loading, reload } = usePlanView<RoadmapData>(() => apiService.getPlanRoadmap(quarter), `${quarter ?? ''}`);
  const goals = (data?.goals ?? []).filter((g) => !projectKey || g.project_key === projectKey);
  return (
    <ViewState loading={loading && !data} error={error} onRetry={reload} empty={data && goals.length === 0 ? 'No goals in this quarter yet.' : data && data.sprints.length === 0 ? 'This quarter has no sprints yet.' : null}>
      {data && (
        <section className={`${CARD} overflow-hidden`} aria-label={`${data.quarter.label} roadmap`}>
          <p className="px-5 pt-4 text-sm text-gray-700">
            Each cell is the goal's tickets that are in that sprint now, and how many of them are done. A ticket carried over shows in every sprint it has been in.
          </p>
          <div className="relative overflow-x-auto">
            <table className="mt-3 w-full border-separate border-spacing-0 text-left text-sm">
              <caption className="sr-only">Goals by sprint</caption>
              <thead>
                <tr className="text-xs font-semibold text-gray-600">
                  <th scope="col" className="sticky left-0 z-10 min-w-[12rem] border-b border-gray-200 bg-white px-4 py-2 uppercase tracking-wider sm:min-w-[18rem]">Goal</th>
                  {data.sprints.map((s) => (
                    <th key={s.label} scope="col" className={`min-w-[6.5rem] border-b border-gray-200 px-3 py-2 ${s.current ? 'bg-blue-50 text-blue-800' : ''}`}>
                      <span className="block uppercase tracking-wider">{s.label}</span>
                      <span className="block font-normal normal-case">
                        {s.start && s.end ? `${fmt.dayMonth(s.start)} to ${fmt.dayMonth(s.end)}` : 'No dates yet'}
                        {s.current ? ' · now' : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {goals.map((g) => (
                  <tr key={g.key} className="hover:bg-gray-50">
                    <th scope="row" className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-2.5 text-left align-top font-normal">
                      <button type="button" onClick={() => onOpen(g.key)} className="block max-w-[18rem] break-words rounded text-left leading-snug text-gray-900 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                        {g.title}
                      </button>
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.6875rem] text-gray-600">
                        <span>{g.project_name ?? g.project_key}</span>
                        <StatusPill row={{ plan_status: g.plan_status, jira_status: null, sheet_status: null }} />
                      </span>
                    </th>
                    {data.sprints.map((s) => {
                      const c = g.cells[s.label];
                      return (
                        <td key={s.label} className={`border-b border-gray-100 px-3 py-2.5 align-top ${s.current ? 'bg-blue-50/40' : ''}`}>
                          {c ? (
                            <span aria-label={`${fmt.count(c.done)} of ${fmt.count(c.tickets)} tickets done`}>
                              <span className="font-medium tabular-nums text-gray-900">{fmt.count(c.tickets)}</span>
                              <span className="block text-xs tabular-nums text-gray-600">{fmt.count(c.done)} done</span>
                            </span>
                          ) : (
                            <span aria-hidden className="text-gray-400">–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </ViewState>
  );
}
